/**
 * Risk Management Storage Utility
 * 
 * Handles saving and loading risk management settings to localStorage
 */

// Storage keys
const STORAGE_PREFIX = 'tradingview_risk_';
const POSITION_SIZE_KEY = `${STORAGE_PREFIX}position_size`;
const STOP_LOSS_KEY = `${STORAGE_PREFIX}stop_loss`;
const RISK_REWARD_KEY = `${STORAGE_PREFIX}risk_reward`;
const MARGIN_POSITION_KEY = `${STORAGE_PREFIX}margin_position`;
const GENERAL_SETTINGS_KEY = `${STORAGE_PREFIX}general`;

/**
 * Save data to localStorage with error handling
 * 
 * @param {string} key - Storage key
 * @param {Object} data - Data to store
 * @returns {boolean} Success status
 */
const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    return false;
  }
};

/**
 * Load data from localStorage with error handling
 * 
 * @param {string} key - Storage key
 * @param {Object} defaultValue - Default value if not found
 * @returns {Object} Stored data or default
 */
const loadFromStorage = (key, defaultValue = null) => {
  try {
    const storedData = localStorage.getItem(key);
    return storedData ? JSON.parse(storedData) : defaultValue;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return defaultValue;
  }
};

/**
 * Position Size Calculator Storage
 */
export const PositionSizeStorage = {
  // Save position size calculator settings
  saveSettings: (settings) => {
    return saveToStorage(POSITION_SIZE_KEY, settings);
  },
  
  // Load position size calculator settings
  loadSettings: () => {
    return loadFromStorage(POSITION_SIZE_KEY, {
      accountSize: 10000,
      riskPercentage: 1,
      entryPrice: 100,
      stopLossPrice: 95,
      direction: 'long',
      useFixedRisk: false,
      fixedRiskAmount: 100,
      winRate: 50,
      rewardRatio: 2
    });
  }
};

/**
 * Margin Position Calculator Storage
 */
export const MarginPositionStorage = {
  // Save margin position calculator settings
  saveSettings: (settings) => {
    return saveToStorage(MARGIN_POSITION_KEY, settings);
  },
  
  // Load margin position calculator settings
  loadSettings: () => {
    return loadFromStorage(MARGIN_POSITION_KEY, {
      accountSize: 10000,
      riskPercentage: 1,
      availableMargin: 10000,
      leverage: 10,
      entryPrice: 100,
      stopLossPrice: 95,
      direction: 'long',
      marginMode: 'isolated'
    });
  }
};

/**
 * Stop Loss Calculator Storage
 */
export const StopLossStorage = {
  // Save stop loss calculator settings
  saveSettings: (settings) => {
    return saveToStorage(STOP_LOSS_KEY, settings);
  },
  
  // Load stop loss calculator settings
  loadSettings: () => {
    return loadFromStorage(STOP_LOSS_KEY, {
      entryPrice: 100,
      isLong: true,
      method: 'percentage',
      atrValue: 1,
      atrMultiplier: 2,
      percentageValue: 5,
      technicalLevel: 95,
      showAll: false
    });
  }
};

/**
 * Risk Reward Visualizer Storage
 */
export const RiskRewardStorage = {
  // Save risk reward visualizer settings
  saveSettings: (settings) => {
    return saveToStorage(RISK_REWARD_KEY, settings);
  },
  
  // Load risk reward visualizer settings
  loadSettings: () => {
    return loadFromStorage(RISK_REWARD_KEY, {
      entryPrice: 100,
      stopLossPrice: 95,
      direction: 'long',
      winRate: 50,
      customTargets: false,
      takeProfitPrice1: 110,
      takeProfitPrice2: 115,
      takeProfitPrice3: 120
    });
  }
};

/**
 * General Risk Management Settings
 */
export const RiskSettingsStorage = {
  // Save active tab
  saveActiveTab: (tabKey) => {
    const settings = loadFromStorage(GENERAL_SETTINGS_KEY, {});
    settings.activeTab = tabKey;
    return saveToStorage(GENERAL_SETTINGS_KEY, settings);
  },
  
  // Load active tab
  loadActiveTab: () => {
    const settings = loadFromStorage(GENERAL_SETTINGS_KEY, {});
    return settings.activeTab || 'position-size';
  },
  
  // Get common settings that might be used across calculators
  getCommonSettings: () => {
    // First check if any calculator has saved settings
    const positionSettings = loadFromStorage(POSITION_SIZE_KEY, null);
    const stopLossSettings = loadFromStorage(STOP_LOSS_KEY, null);
    const riskRewardSettings = loadFromStorage(RISK_REWARD_KEY, null);
    const marginSettings = loadFromStorage(MARGIN_POSITION_KEY, null);
    
    // Combine common settings (account size, entry/stop prices)
    return {
      accountSize: marginSettings?.accountSize || 
                  positionSettings?.accountSize || 
                  10000,
      entryPrice: marginSettings?.entryPrice || 
                  positionSettings?.entryPrice || 
                  stopLossSettings?.entryPrice || 
                  riskRewardSettings?.entryPrice || 
                  100,
      stopLossPrice: marginSettings?.stopLossPrice || 
                     positionSettings?.stopLossPrice || 
                     riskRewardSettings?.stopLossPrice || 
                     95,
      direction: marginSettings?.direction || 
                 positionSettings?.direction || 
                 (stopLossSettings?.isLong ? 'long' : 'short') || 
                 riskRewardSettings?.direction || 
                 'long',
      winRate: positionSettings?.winRate || 
               riskRewardSettings?.winRate || 
               50
    };
  }
};

const riskStorage = {
    PositionSizeStorage,
    MarginPositionStorage,
    StopLossStorage,
    RiskRewardStorage,
    RiskSettingsStorage
  };

export default riskStorage